import { toast } from "react-toastify";
import { GetEncounter, PostEncounter } from "@/services/fhir";
import { PatchEncounterStatus } from "@/services/fhir/encounter/PatchEncounterStatus";
import type { IEncounterData } from "@/utils/interfaces";
import { patchEncounterPartOf } from "./patchFhirActors";
import {
  isSamePractitionerReference,
  isTeleconsultoriaParticipant,
  teleconsultoriaGuestOfAppointment,
} from "../utils/actors";
import {
  pickGuestEncounter,
  pickHostEncounter,
} from "../utils/teleconsultoriaEncounters";

interface ResolveTeleconsultorEncounterParams {
  appointment: fhir5.Appointment;
  patientId: string;
  /** Practitioner logado: `Practitioner/{oid}-{cpf}`. */
  practitionerRef: string;
  /** Mesmo payload montado pelo verifyEncounter para o host. */
  encounterData: IEncounterData;
}

interface TeleconsultorEncounterResult {
  encounter: fhir5.Encounter;
  hostEncounterId: string;
}

/**
 * Ramo do teleconsultor no verifyEncounter.
 * 1. Só o teleconsultor convidado (marcado CON) pode seguir.
 * 2. Reaproveita o Encounter do teleconsultor já ligado ao host (GET inicial).
 * 3. Senão: POST sem partOf (gera o consentimento), GET do host e PATCH do partOf.
 *    Sem host ou em falha, o Encounter criado vira entered-in-error.
 * Devolve undefined quando não pode seguir (o toast já foi exibido).
 */
export async function resolveTeleconsultorEncounter({
  appointment,
  patientId,
  practitionerRef,
  encounterData,
}: ResolveTeleconsultorEncounterParams): Promise<
  TeleconsultorEncounterResult | undefined
> {
  const guest = teleconsultoriaGuestOfAppointment(appointment).practitioner;
  // Comparação pelo CPF: o formato da reference pode variar entre servidores.
  if (!guest?.reference || !isSamePractitionerReference(guest.reference, practitionerRef)) {
    toast.warning("Você não é o teleconsultor convidado deste atendimento.");
    return undefined;
  }

  // Id do Encounter criado neste clique: em falha, vira entered-in-error.
  let createdId: string | undefined;
  try {
    // 1. GET inicial só para continuar. Antes do 1º POST (sem consentimento)
    //    volta vazio; depois enxerga o Encounter já ligado ao host.
    const first = await listAppointmentEncounters(patientId, appointment.id);
    const firstHost = pickHostEncounter(first);
    const existing = firstHost?.id
      ? pickGuestEncounter(first, firstHost.id, practitionerRef)
      : undefined;
    if (existing && firstHost?.id) {
      return { encounter: existing, hostEncounterId: firstHost.id };
    }

    // 2. POST sem partOf: gera o consentimento do teleconsultor no backend.
    //    Teleconsultoria é sempre por vídeo: classe VR (virtual).
    const created: fhir5.Encounter = await PostEncounter({
      ...encounterData,
      asTeleconsultor: true,
      attendanceClass: "VR",
    });
    createdId = created?.id;
    if (!createdId) throw new Error("Encounter do teleconsultor sem id");

    // 3. GET de novo (agora funciona), ignorando o Encounter recém-criado.
    const second = await listAppointmentEncounters(patientId, appointment.id);
    const host = pickHostEncounter(second, createdId);

    // 4. Sem Encounter do host: o recém-criado não pode ficar ativo sem partOf.
    if (!host?.id) {
      await markEnteredInError(createdId);
      toast.info("O profissional de origem ainda não iniciou o atendimento.");
      return undefined;
    }

    // 5. Liga o Encounter do teleconsultor ao do host.
    await patchEncounterPartOf(createdId, host.id);

    // 6. Mesmo shape de antes para o verifyEncounter.
    return {
      encounter: { ...created, partOf: { reference: `Encounter/${host.id}` } },
      hostEncounterId: host.id,
    };
  } catch (error) {
    console.error("resolveTeleconsultorEncounter", error);
    // Falha depois do POST (GET/PATCH): não deixa Encounter ativo sem partOf.
    if (createdId) await markEnteredInError(createdId);
    toast.error("Não foi possível iniciar a teleconsultoria. Tente novamente.");
    return undefined;
  }
}

/** count 20: o Appointment pode ter o Encounter do host e o do teleconsultor. */
async function listAppointmentEncounters(
  patientId: string,
  appointmentId: string | undefined,
): Promise<Array<{ resource?: fhir5.Encounter }> | undefined> {
  const bundle = await GetEncounter({
    patientId,
    appointmentId,
    lastUpdaded: true,
    count: 20,
  });
  return bundle?.entry;
}

/**
 * Best effort: Encounter órfão do teleconsultor vira entered-in-error.
 * pickHostEncounter e o histórico ignoram esse status. Nunca lança erro,
 * para não esconder o toast principal.
 */
async function markEnteredInError(encounterId: string) {
  try {
    await PatchEncounterStatus({ encounterId, status: "entered-in-error" });
  } catch (error) {
    console.error("markEnteredInError", encounterId, error);
  }
}

/**
 * Participants do encounterReference do teleconsultor: marcados CON primeiro.
 * Assim findParticipant("Practitioner") (autor no Footer/documentos) é o teleconsultor.
 */
export function teleconsultorFirstParticipants(
  participants: fhir5.AppointmentParticipant[] | undefined,
): fhir5.AppointmentParticipant[] {
  const list = participants || [];
  // Sem marcação CON (servidor devolveu sem `type`): só é chamado no atendimento
  // do teleconsultor, que é o Practitioner depois do host (regra da ordem).
  if (!list.some(isTeleconsultoriaParticipant)) {
    const practitioners = list.filter((item) => item.actor?.reference?.includes("Practitioner/"));
    if (practitioners.length < 2) return list;
    const guests = practitioners.slice(1);
    return [...guests, ...list.filter((item) => !guests.includes(item))];
  }
  return [
    ...list.filter(isTeleconsultoriaParticipant),
    ...list.filter((item) => !isTeleconsultoriaParticipant(item)),
  ];
}
